import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../config/api'; 

export default function SaveProductButton({ productId, initialIsSaved, onSaveStatusChange }) {
  const { isAuthenticated, authToken } = useAuth();
  const [isSaved, setIsSaved] = useState(initialIsSaved || false);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState({ message: '', type: '' });
  const feedbackTimer = useRef(null);

  useEffect(() => {
    setIsSaved(initialIsSaved || false);
  }, [initialIsSaved]);

  useEffect(() => {
    return () => { if (feedbackTimer.current) clearTimeout(feedbackTimer.current); };
  }, []);

  const showFeedback = (message, type = 'success') => {
    setFeedback({ message, type });
    if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
    feedbackTimer.current = setTimeout(() => setFeedback({ message: '', type: '' }), 3000);
  };

  const handleSaveToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated || !productId) return;

    setLoading(true);
    const url = `${API_BASE_URL}/products/${productId}/${isSaved ? 'unsave' : 'save'}`;
    const method = isSaved ? 'DELETE' : 'POST';

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (response.ok) {
        const newSavedStatus = !isSaved;
        setIsSaved(newSavedStatus);
        if (onSaveStatusChange) {
          onSaveStatusChange(productId, newSavedStatus);
        }
        showFeedback(newSavedStatus ? 'Watching price' : 'Removed', 'success');
      } else {
        showFeedback(data.message || 'Could not update.', 'error');
      }
    } catch (err) {
      console.error('Save/unsave error:', err);
      showFeedback('Network error. Try again.', 'error');
    }
    setLoading(false);
  };

  if (!isAuthenticated) {
    return (
      <Link to="/login" 
        onClick={(e) => { e.stopPropagation(); }}
        className="mt-auto w-full block text-center text-xs text-red-800 hover:text-red-900 hover:underline py-1.5">
        Login to watch price
      </Link>
    );
  }

  return (
    <div className="w-full">
      <button 
        onClick={handleSaveToggle}
        disabled={loading}
        className={`w-full mt-2 text-xs font-semibold py-2 px-3 rounded-md transition duration-150 ease-in-out min-h-[36px]
                    ${isSaved 
                      ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200' 
                      : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'}
                    disabled:opacity-60`}
      >
        {loading ? (
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
            Saving...
          </span>
        ) : (isSaved ? '♥ Watching Price' : '♡ Watch Price')}
      </button>
      {feedback.message && (
        <p className={`text-xs mt-1 text-center transition-opacity duration-200 ${
          feedback.type === 'error' ? 'text-red-500' : 'text-green-600'
        }`}>
          {feedback.message}
        </p>
      )}
    </div>
  );
} 